import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload, faCheck, faSync, faTimes } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import './ImportarUsuarios.css';
import baseURL from '../../../url';

export default function ImportarUsuarios({ modalImportacionVisible, setModalImportacionVisible, cargarUsuarios }) {
    const [pasoImportacion, setPasoImportacion] = useState(1);
    const [archivoExcel, setArchivoExcel] = useState(null);
    const [datosImportacion, setDatosImportacion] = useState([]);
    const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);
    const [importando, setImportando] = useState(false);
    const [todosSeleccionados, setTodosSeleccionados] = useState(false);
    const [erroresValidacion, setErroresValidacion] = useState({});
    const [accessCodesExistentes, setAccessCodesExistentes] = useState([]);
    const [serviciosData, setServiciosData] = useState([]);

    useEffect(() => {
        if (modalImportacionVisible) {
            cargarAccessCodesExistentes();
            cargarServicios();
        }
    }, [modalImportacionVisible]);

    const cargarAccessCodesExistentes = async () => {
        try {
            const response = await fetch(`${baseURL}/usuariosGet.php`, { method: 'GET' });
            const data = await response.json();
            // Convertir todos los access_code a string para comparaciones consistentes
            const codes = (data.usuarios || []).map(u => u.access_code ? String(u.access_code).trim() : '').filter(code => code);
            setAccessCodesExistentes(codes);
        } catch (error) {
            console.error('Error al cargar access codes existentes:', error);
        }
    };

    const cargarServicios = async () => {
        try {
            const response = await fetch(`${baseURL}/serviciosGet.php`, { method: 'GET' });
            const data = await response.json();
            setServiciosData(data.servicios || []);
        } catch (error) {
            console.error('Error al cargar servicios:', error);
        }
    };

    const obtenerIdCesecoDesdePrograma = (idPrograma) => {
        if (!idPrograma || idPrograma === '') return null;
        const servicio = serviciosData.find(s => s.idServicio === parseInt(idPrograma));
        return servicio ? servicio.idCategoria : null;
    };

    const generarContrasena = () => {
        const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let contrasena = '';
        for (let i = 0; i < 12; i++) {
            contrasena += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
        }
        return contrasena;
    };

    const formatearTelefono = (telefono) => {
        if (!telefono) return '';
        // Remover todos los caracteres no numéricos
        let telefonoLimpio = telefono.toString().replace(/\D/g, '');
        
        // Si no inicia con 52, agregarlo
        if (!telefonoLimpio.startsWith('52')) {
            telefonoLimpio = '52' + telefonoLimpio;
        }
        
        // Asegurar que tenga exactamente 11 dígitos (52 + 9 dígitos)
        if (telefonoLimpio.length > 11) {
            telefonoLimpio = telefonoLimpio.substring(0, 11);
        }
        
        return telefonoLimpio;
    };

    const cerrarModalImportacion = () => {
        setModalImportacionVisible(false);
        setPasoImportacion(1);
        setArchivoExcel(null);
        setDatosImportacion([]);
        setUsuariosSeleccionados([]);
        setErroresValidacion({});
    };

    const descargarPlantilla = async () => {
        try {
            // Leer el archivo CSV de la plantilla
            const response = await fetch(`${baseURL}/templates/plantilla_importacion_usuarios.csv`);
            const csvText = await response.text();
            
            // Parsear CSV
            const lines = csvText.split('\n').filter(line => line.trim());
            const headers = lines[0].split(',').map(h => h.replace(/"/g, ''));
            
            // Crear datos de ejemplo
            const ejemplo1 = ['Juan Pérez García', 'EMP001', 'juan.perez@example.com', 'secretaria', '52425630813', 'activo', '1', '', '0'];
            const ejemplo2 = ['María González López', 'EMP002', 'maria.gonzalez@example.com', 'terapeuta', '52425630814', 'activo', '1', '5', '0'];
            const vacias = Array.from({length: 48}, (_, i) => [`Usuario ${i+3}`, `EMP${String(i+3).padStart(3, '0')}`, `usuario${i+3}@example.com`, 'secretaria', '52425630815', 'activo', '', '', '0']);
            
            const data = [headers, ejemplo1, ejemplo2, ...vacias];
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
            XLSX.writeFile(wb, 'plantilla_importar_usuarios.xlsx');
            toast.success('Plantilla descargada correctamente');
        } catch (error) {
            console.error('Error al descargar plantilla:', error);
            toast.error('Error al descargar la plantilla');
        }
    };

    const procesarArchivoExcel = (file) => {
        const reader = new FileReader();
        const extension = file.name.split('.').pop().toLowerCase();
        
        if (extension === 'csv') {
            // Procesar CSV con encoding UTF-8
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    // Leer CSV con encoding UTF-8
                    const workbook = XLSX.read(text, { 
                        type: 'string',
                        codepage: 65001, // UTF-8
                        FS: ',', // Delimitador
                        RS: '\n' // Separador de filas
                    });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,
                        raw: false,
                        defval: ''
                    });
                    procesarDatos(jsonData);
                } catch (error) {
                    console.error('Error al procesar CSV:', error);
                    toast.error('Error al procesar el archivo CSV');
                }
            };
            // Leer como texto con encoding UTF-8
            reader.readAsText(file, 'UTF-8');
        } else {
            // Procesar Excel
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    // Configurar opciones para leer correctamente UTF-8
                    const workbook = XLSX.read(data, { 
                        type: 'array',
                        codepage: 65001 // UTF-8
                    });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    // Leer con opciones UTF-8
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,
                        raw: false, // Convertir todo a string para preservar UTF-8
                        defval: '' // Valor por defecto
                    });
                    procesarDatos(jsonData);
                } catch (error) {
                    console.error('Error al procesar archivo:', error);
                    toast.error('Error al procesar el archivo Excel');
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };
    
    const procesarDatos = (jsonData) => {
        try {
            const headers = jsonData[0];
            const usuarios = [];
            
            for (let i = 1; i < jsonData.length; i++) {
                const row = jsonData[i];
                if (row.some(cell => cell !== undefined && cell !== '')) {
                    const usuario = {};
                    headers.forEach((header, index) => {
                        const value = row[index];
                        // Convertir campos de texto a string, mantener números como están
                        if (header === 'nombre' || header === 'access_code' || header === 'email' || 
                            header === 'rol' || header === 'telefono' || header === 'estatus') {
                            usuario[header] = value !== undefined && value !== null ? String(value) : '';
                        } else if (header === 'idCeseco' || header === 'idPrograma' || header === 'permite_grupal') {
                            // Campos numéricos: convertir a string vacío si está vacío, o mantener el valor
                            usuario[header] = value !== undefined && value !== null && value !== '' ? String(value) : '';
                        } else {
                            usuario[header] = value !== undefined && value !== null ? value : '';
                        }
                    });
                    
                    // Procesar y validar datos
                    // Obtener idCeseco desde idPrograma si no está presente
                    if (!usuario.idCeseco || usuario.idCeseco === '') {
                        if (usuario.idPrograma && usuario.idPrograma !== '') {
                            const idCeseco = obtenerIdCesecoDesdePrograma(usuario.idPrograma);
                            if (idCeseco) {
                                usuario.idCeseco = idCeseco.toString();
                            }
                        }
                    }
                    
                    // Formatear teléfono solo si se proporciona
                    if (usuario.telefono && usuario.telefono.trim() !== '') {
                        usuario.telefono = formatearTelefono(usuario.telefono);
                    }
                    
                    // Generar contraseña
                    usuario.contrasena = generarContrasena();
                    
                    usuarios.push(usuario);
                }
            }
            
            setDatosImportacion(usuarios);
            setPasoImportacion(2);
            toast.success(`${usuarios.length} usuarios encontrados en el archivo`);
        } catch (error) {
            console.error('Error al procesar datos:', error);
            toast.error('Error al procesar los datos del archivo');
        }
    };

    const toggleUsuarioSeleccionado = (index) => {
        setUsuariosSeleccionados(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    const seleccionarTodos = () => {
        setUsuariosSeleccionados(datosImportacion.map((_, index) => index));
    };

    const deseleccionarTodos = () => {
        setUsuariosSeleccionados([]);
    };

    const validarUsuario = (usuario, index) => {
        const errores = {};
        
        // Convertir valores a string para evitar errores con .trim()
        const nombre = usuario.nombre ? String(usuario.nombre) : '';
        const access_code = usuario.access_code ? String(usuario.access_code) : '';
        const email = usuario.email ? String(usuario.email) : '';
        const telefono = usuario.telefono ? String(usuario.telefono) : '';
        const rol = usuario.rol ? String(usuario.rol) : '';
        
        // Nombre
        if (!nombre || nombre.trim().length < 3) {
            errores.nombre = 'El nombre debe tener al menos 3 caracteres';
        }
        
        // Access code
        if (!access_code || access_code.trim().length === 0) {
            errores.access_code = 'El código de acceso es obligatorio';
        } else if (accessCodesExistentes.includes(access_code.trim())) {
            errores.access_code = 'Este código de acceso ya existe';
        }
        
        // Email (opcional, pero si se proporciona debe ser válido)
        if (email && email.trim().length > 0) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                errores.email = 'El email no tiene un formato válido';
            }
        }
        
        // Rol
        const rolesValidos = ['secretaria', 'terapeuta', 'coord_programa', 'coord_ceseco', 'direccion', 'admin'];
        if (!rol || !rolesValidos.includes(rol.trim())) {
            errores.rol = 'El rol debe ser uno de: ' + rolesValidos.join(', ');
        }
        
        // Teléfono (opcional, pero si se proporciona debe tener formato válido)
        if (telefono && telefono.trim().length > 0) {
            const telefonoLimpio = telefono.replace(/\D/g, '');
            if (telefonoLimpio.length !== 11 || !telefonoLimpio.startsWith('52')) {
                errores.telefono = 'El teléfono debe tener 11 dígitos e iniciar con 52';
            }
        }
        
        // Estatus
        if (!usuario.estatus || (usuario.estatus !== 'activo' && usuario.estatus !== 'inactivo')) {
            errores.estatus = 'El estatus debe ser "activo" o "inactivo"';
        }
        
        // idCeseco (validar según rol)
        if ((usuario.rol === 'coord_ceseco' || usuario.rol === 'coord_programa' || usuario.rol === 'direccion') && (!usuario.idCeseco || usuario.idCeseco === '')) {
            errores.idCeseco = 'Este rol requiere un CESECO';
        }
        
        // idPrograma (validar según rol)
        if ((usuario.rol === 'terapeuta' || usuario.rol === 'coord_programa') && (!usuario.idPrograma || usuario.idPrograma === '')) {
            errores.idPrograma = 'Este rol requiere un programa';
        }
        
        // permite_grupal
        if (usuario.permite_grupal !== '0' && usuario.permite_grupal !== '1' && usuario.permite_grupal !== 0 && usuario.permite_grupal !== 1) {
            errores.permite_grupal = 'Debe ser 0 o 1';
        }
        
        return errores;
    };

    const importarUsuarios = async () => {
        // Validar usuarios seleccionados
        const nuevosErrores = {};
        let hayErrores = false;
        
        usuariosSeleccionados.forEach(index => {
            const usuario = { ...datosImportacion[index] };
            // Actualizar idCeseco si es necesario
            if (!usuario.idCeseco || usuario.idCeseco === '') {
                if (usuario.idPrograma && usuario.idPrograma !== '') {
                    const idCeseco = obtenerIdCesecoDesdePrograma(usuario.idPrograma);
                    if (idCeseco) {
                        usuario.idCeseco = idCeseco.toString();
                    }
                }
            }
            // Formatear teléfono
            if (usuario.telefono) {
                usuario.telefono = formatearTelefono(usuario.telefono);
            }
            
            const errores = validarUsuario(usuario, index);
            if (Object.keys(errores).length > 0) {
                nuevosErrores[index] = errores;
                hayErrores = true;
            }
        });
        
        setErroresValidacion(nuevosErrores);
        if (hayErrores) {
            toast.error('Corrige los errores en los usuarios seleccionados antes de importar.');
            return;
        }
        
        // Filtrar usuarios con access_code duplicados
        const usuariosAImportar = usuariosSeleccionados.map(index => {
            const usuario = { ...datosImportacion[index] };
            // Asegurar que access_code sea string
            if (usuario.access_code) {
                usuario.access_code = String(usuario.access_code).trim();
            }
            // Actualizar idCeseco si es necesario
            if (!usuario.idCeseco || usuario.idCeseco === '') {
                if (usuario.idPrograma && usuario.idPrograma !== '') {
                    const idCeseco = obtenerIdCesecoDesdePrograma(usuario.idPrograma);
                    if (idCeseco) {
                        usuario.idCeseco = idCeseco.toString();
                    }
                }
            }
            // Formatear teléfono
            if (usuario.telefono) {
                usuario.telefono = formatearTelefono(usuario.telefono);
            }
            return usuario;
        });
        
        // Verificar duplicados en el mismo lote (normalizar a string)
        const accessCodesEnLote = usuariosAImportar.map(u => u.access_code ? String(u.access_code).trim() : '');
        const duplicadosEnLote = accessCodesEnLote.filter((code, index) => code && accessCodesEnLote.indexOf(code) !== index);
        
        if (duplicadosEnLote.length > 0) {
            toast.error(`Hay access_code duplicados en el archivo: ${[...new Set(duplicadosEnLote)].join(', ')}`);
            return;
        }
        
        // Verificar duplicados con existentes (normalizar ambos lados a string)
        const accessCodesExistentesNormalizados = accessCodesExistentes.map(code => String(code).trim());
        const duplicadosExistentes = usuariosAImportar.filter(u => {
            const code = u.access_code ? String(u.access_code).trim() : '';
            return code && accessCodesExistentesNormalizados.includes(code);
        });
        let usuariosFinales = usuariosAImportar;
        if (duplicadosExistentes.length > 0) {
            const codes = duplicadosExistentes.map(u => u.access_code).join(', ');
            toast.error(`Los siguientes access_code ya existen y no se importarán: ${codes}`);
            // Filtrar duplicados
            usuariosFinales = usuariosAImportar.filter(u => !accessCodesExistentes.includes(u.access_code));
            if (usuariosFinales.length === 0) {
                toast.error('Todos los usuarios tienen access_code duplicados. No se importará ninguno.');
                return;
            }
        }
        
        setImportando(true);
        try {
            const response = await fetch(`${baseURL}/usuariosImportarMasivo.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ usuarios: usuariosFinales })
            });
            const result = await response.json();
            setImportando(false);
            
            if (result.resultados) {
                const exitos = result.resultados.filter(r => r.status === 'ok');
                const errores = result.resultados.filter(r => r.status === 'error');
                
                // Construir HTML del resumen detallado
                let htmlResumen = `
                    <div style="text-align: left; max-height: 500px; overflow-y: auto;">
                        <div style="margin-bottom: 20px;">
                            <h4 style="color: #28a745; margin-bottom: 10px;">✅ Usuarios Importados Exitosamente: ${exitos.length}</h4>
                            ${exitos.length > 0 ? `
                                <div style="background: #d4edda; padding: 10px; border-radius: 5px; margin-bottom: 10px; max-height: 200px; overflow-y: auto;">
                                    <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                                        <thead>
                                            <tr style="background: #c3e6cb;">
                                                <th style="padding: 8px; text-align: left; border: 1px solid #b1dfbb;">Access Code</th>
                                                <th style="padding: 8px; text-align: left; border: 1px solid #b1dfbb;">Nombre</th>
                                                <th style="padding: 8px; text-align: left; border: 1px solid #b1dfbb;">ID Usuario</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${exitos.map(e => `
                                                <tr>
                                                    <td style="padding: 6px; border: 1px solid #b1dfbb;">${e.access_code || 'N/A'}</td>
                                                    <td style="padding: 6px; border: 1px solid #b1dfbb;">${e.nombre || 'N/A'}</td>
                                                    <td style="padding: 6px; border: 1px solid #b1dfbb;">${e.idUsuario || 'N/A'}</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </div>
                            ` : '<p style="color: #6c757d; font-style: italic;">No se importaron usuarios exitosamente.</p>'}
                        </div>
                `;
                
                if (errores.length > 0) {
                    htmlResumen += `
                        <div style="margin-bottom: 20px;">
                            <h4 style="color: #dc3545; margin-bottom: 10px;">❌ Usuarios No Importados: ${errores.length}</h4>
                            <div style="background: #f8d7da; padding: 10px; border-radius: 5px; margin-bottom: 10px; max-height: 200px; overflow-y: auto;">
                                <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                                    <thead>
                                        <tr style="background: #f5c6cb;">
                                            <th style="padding: 8px; text-align: left; border: 1px solid #f1aeb5;">Access Code</th>
                                            <th style="padding: 8px; text-align: left; border: 1px solid #f1aeb5;">Nombre</th>
                                            <th style="padding: 8px; text-align: left; border: 1px solid #f1aeb5;">Razón</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${errores.map(e => `
                                            <tr>
                                                <td style="padding: 6px; border: 1px solid #f1aeb5;">${e.access_code || 'N/A'}</td>
                                                <td style="padding: 6px; border: 1px solid #f1aeb5;">${e.nombre || 'N/A'}</td>
                                                <td style="padding: 6px; border: 1px solid #f1aeb5; color: #721c24;">${e.mensaje || 'Error desconocido'}</td>
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    `;
                }
                
                if (duplicadosExistentes.length > 0) {
                    htmlResumen += `
                        <div style="margin-bottom: 20px;">
                            <h4 style="color: #ffc107; margin-bottom: 10px;">⚠️ Usuarios Omitidos (Access Code Duplicado): ${duplicadosExistentes.length}</h4>
                            <div style="background: #fff3cd; padding: 10px; border-radius: 5px; margin-bottom: 10px; max-height: 200px; overflow-y: auto;">
                                <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                                    <thead>
                                        <tr style="background: #ffeaa7;">
                                            <th style="padding: 8px; text-align: left; border: 1px solid #ffd93d;">Access Code</th>
                                            <th style="padding: 8px; text-align: left; border: 1px solid #ffd93d;">Nombre</th>
                                            <th style="padding: 8px; text-align: left; border: 1px solid #ffd93d;">Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${duplicadosExistentes.map(u => `
                                            <tr>
                                                <td style="padding: 6px; border: 1px solid #ffd93d;">${u.access_code || 'N/A'}</td>
                                                <td style="padding: 6px; border: 1px solid #ffd93d;">${u.nombre || 'N/A'}</td>
                                                <td style="padding: 6px; border: 1px solid #ffd93d;">${u.email || 'N/A'}</td>
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    `;
                }
                
                htmlResumen += '</div>';
                
                // Resumen numérico al inicio
                const totalProcesados = exitos.length + errores.length;
                const totalOmitidos = duplicadosExistentes.length;
                const mensajeResumen = `
                    <div style="background: #e7f3ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #2196F3;">
                        <p style="margin: 5px 0; font-weight: 600; color: #1976D2;">
                            📊 Resumen de Importación:
                        </p>
                        <p style="margin: 5px 0;">✅ <strong>${exitos.length}</strong> usuarios importados exitosamente</p>
                        ${errores.length > 0 ? `<p style="margin: 5px 0;">❌ <strong>${errores.length}</strong> usuarios no se importaron</p>` : ''}
                        ${totalOmitidos > 0 ? `<p style="margin: 5px 0;">⚠️ <strong>${totalOmitidos}</strong> usuarios omitidos (duplicados)</p>` : ''}
                        <p style="margin: 5px 0; margin-top: 10px; font-weight: 600;">Total procesado: <strong>${totalProcesados + totalOmitidos}</strong> usuarios</p>
                    </div>
                `;
                
                Swal.fire({
                    title: 'Importación Finalizada',
                    html: mensajeResumen + htmlResumen,
                    icon: exitos.length > 0 ? 'success' : 'warning',
                    width: '800px',
                    confirmButtonText: 'Aceptar',
                    customClass: {
                        popup: 'swal-wide'
                    }
                }).then((result) => {
                    if (exitos.length > 0) {
                        setTimeout(() => {
                            cerrarModalImportacion();
                            cargarUsuarios();
                        }, 100);
                    }
                });
            } else if (result.error) {
                toast.error(result.error);
            } else {
                toast.error('Error inesperado al importar.');
            }
        } catch (error) {
            setImportando(false);
            toast.error('Error durante la importación');
            console.error('Error:', error);
        }
    };

    const handleExcelFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const extension = file.name.split('.').pop().toLowerCase();
            if (extension === 'xlsx' || extension === 'xls' || extension === 'csv') {
                setArchivoExcel(file);
                procesarArchivoExcel(file);
            } else {
                toast.error('Por favor seleccione un archivo Excel o CSV válido (.xlsx, .xls o .csv)');
                event.target.value = '';
            }
        }
    };

    const handleInputChange = (rowIndex, field, value) => {
        setDatosImportacion(prev => {
            const nuevos = prev.map((usuario, idx) => {
                if (idx === rowIndex) {
                    const actualizado = { ...usuario, [field]: value };
                    
                    // Si cambia idPrograma y no hay idCeseco, obtenerlo
                    if (field === 'idPrograma' && (!actualizado.idCeseco || actualizado.idCeseco === '')) {
                        const idCeseco = obtenerIdCesecoDesdePrograma(value);
                        if (idCeseco) {
                            actualizado.idCeseco = idCeseco.toString();
                        }
                    }
                    
                    // Si cambia el teléfono, formatearlo
                    if (field === 'telefono') {
                        actualizado.telefono = formatearTelefono(value);
                    }
                    
                    return actualizado;
                }
                return usuario;
            });
            return nuevos;
        });
    };

    if (!modalImportacionVisible) return null;

    return (
        <div className="modalImportacion">
            <div className="modal-content-importacion">
                <div className="modal-header">
                    <div className="header-title-actions">
                        <h3>Importar Usuarios</h3>
                        <div className="header-actions">
                            <span className="close" onClick={cerrarModalImportacion}>&times;</span>
                        </div>
                    </div>
                </div>
                {pasoImportacion === 1 && (
                    <div className="paso-1">
                        <div className="paso-indicador">
                            <span className="paso-activo">1. Descargar Plantilla</span>
                            <span className="paso-inactivo">2. Cargar y Revisar</span>
                        </div>
                        <div className="instrucciones">
                            <h4>Instrucciones:</h4>
                            <ul>
                                <li>Descarga la plantilla Excel con la estructura correcta</li>
                                <li>Completa los datos de los usuarios</li>
                                <li>Guarda el archivo y súbelo en el siguiente paso</li>
                                <li>Los teléfonos deben tener 11 dígitos e iniciar con 52 (ejemplo: 52425630813)</li>
                                <li>Si no incluyes idCeseco, se obtendrá automáticamente desde idPrograma</li>
                                <li>Las contraseñas se generarán automáticamente</li>
                            </ul>
                        </div>
                        <div className="botones-accion">
                            <button className="btn-descargar" onClick={descargarPlantilla}>
                                <FontAwesomeIcon icon={faDownload} />
                                Descargar Plantilla
                            </button>
                            <div className="subir-archivo">
                                <label htmlFor="excel-file-import" className="btn-subir">
                                    <FontAwesomeIcon icon={faUpload} />
                                    Seleccionar Archivo Excel/CSV
                                </label>
                                <input
                                    id="excel-file-import"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleExcelFileChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>
                        {archivoExcel && (
                            <div className="archivo-seleccionado">
                                <FontAwesomeIcon icon={faCheck} />
                                <span>Archivo seleccionado: {archivoExcel.name}</span>
                            </div>
                        )}
                    </div>
                )}
                {pasoImportacion === 2 && (
                    <div className="paso-2">
                        <div className="paso-indicador">
                            <span className="paso-completado">1. Descargar Plantilla</span>
                            <span className="paso-activo">2. Cargar y Revisar</span>
                        </div>
                        <div className="resumen-edicion">
                            <span>✏️</span>
                            <span>Puedes <strong>editar cualquier campo</strong> directamente en la tabla antes de importar. Los campos obligatorios vacíos se marcarán en rojo. Los usuarios con <strong>access_code duplicados</strong> no se importarán.</span>
                        </div>
                        <div className="usuarios-preview">
                            <div className="header-preview">
                                <h4>Usuarios a Importar ({datosImportacion.length})</h4>
                                <div className="acciones-masivas">
                                    <label className="switch-toggle">
                                        <input
                                            type="checkbox"
                                            checked={todosSeleccionados}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    seleccionarTodos();
                                                } else {
                                                    deseleccionarTodos();
                                                }
                                                setTodosSeleccionados(e.target.checked);
                                            }}
                                        />
                                        <span className="slider"></span>
                                        <span className="switch-label">{todosSeleccionados ? 'Deseleccionar Todos' : 'Seleccionar Todos'}</span>
                                    </label>
                                </div>
                                <div className="acciones-importacion">
                                    <button 
                                        className="btn-finalizar btn-sm" 
                                        onClick={importarUsuarios}
                                        disabled={usuariosSeleccionados.length === 0 || importando}
                                    >
                                        {importando ? (
                                            <>
                                                <FontAwesomeIcon icon={faSync} spin />
                                                Importando...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUpload} />
                                                Importar {usuariosSeleccionados.length} Usuarios
                                            </>
                                        )}
                                    </button>
                                    <button className="btn-cancelar btn-sm" onClick={cerrarModalImportacion}>
                                        <FontAwesomeIcon icon={faTimes} />
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                            <div className="tabla-preview">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    checked={usuariosSeleccionados.length === datosImportacion.length && datosImportacion.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            seleccionarTodos();
                                                            setTodosSeleccionados(true);
                                                        } else {
                                                            deseleccionarTodos();
                                                            setTodosSeleccionados(false);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>Nombre</th>
                                            <th>Access Code</th>
                                            <th>Email</th>
                                            <th>Rol</th>
                                            <th>Teléfono</th>
                                            <th>Estatus</th>
                                            <th>idCeseco</th>
                                            <th>idPrograma</th>
                                            <th>Permite Grupal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datosImportacion.map((usuario, index) => {
                                            const errores = erroresValidacion[index] || {};
                                            const isError = (field) => errores[field] !== undefined;
                                            
                                            return (
                                                <tr key={index} className={usuariosSeleccionados.includes(index) ? 'seleccionado' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={usuariosSeleccionados.includes(index)}
                                                            onChange={() => toggleUsuarioSeleccionado(index)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={usuario.nombre || ''}
                                                            onChange={e => handleInputChange(index, 'nombre', e.target.value)}
                                                            className={isError('nombre') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={usuario.access_code || ''}
                                                            onChange={e => handleInputChange(index, 'access_code', e.target.value)}
                                                            className={isError('access_code') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="email"
                                                            value={usuario.email || ''}
                                                            onChange={e => handleInputChange(index, 'email', e.target.value)}
                                                            className={isError('email') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={usuario.rol || ''}
                                                            onChange={e => handleInputChange(index, 'rol', e.target.value)}
                                                            className={isError('rol') ? 'input-error' : ''}
                                                        >
                                                            <option value="">-</option>
                                                            <option value="secretaria">Secretaria</option>
                                                            <option value="terapeuta">Terapeuta</option>
                                                            <option value="coord_programa">Coord. programa</option>
                                                            <option value="coord_ceseco">Coord. CESECO</option>
                                                            <option value="direccion">Dirección</option>
                                                            <option value="admin">Admin</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={usuario.telefono || ''}
                                                            onChange={e => handleInputChange(index, 'telefono', e.target.value)}
                                                            className={isError('telefono') ? 'input-error' : ''}
                                                            placeholder="52425630813"
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={usuario.estatus || ''}
                                                            onChange={e => handleInputChange(index, 'estatus', e.target.value)}
                                                            className={isError('estatus') ? 'input-error' : ''}
                                                        >
                                                            <option value="">-</option>
                                                            <option value="activo">Activo</option>
                                                            <option value="inactivo">Inactivo</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={usuario.idCeseco || ''}
                                                            onChange={e => handleInputChange(index, 'idCeseco', e.target.value)}
                                                            className={isError('idCeseco') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={usuario.idPrograma || ''}
                                                            onChange={e => handleInputChange(index, 'idPrograma', e.target.value)}
                                                            className={isError('idPrograma') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            value={usuario.permite_grupal || '0'}
                                                            onChange={e => handleInputChange(index, 'permite_grupal', e.target.value)}
                                                            className={isError('permite_grupal') ? 'input-error' : ''}
                                                        >
                                                            <option value="0">0</option>
                                                            <option value="1">1</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

